import React, { useState } from 'react';
import { actions, overlays } from '@ecosystem/newron-design-system';
import { UpsellContent } from './types';
import brandsImage from '../../assets/images/brands.png';
import featuresImage from '../../assets/images/features.png';
import priceclassesImage from '../../assets/images/priceclasses.png';
import channelsImage from '../../assets/images/channels.png';
import firstActivityImage from '../../assets/images/firstactivity.png';
import productLeaderboardImage from '../../assets/images/productleaderboard.png';
import { triggerUpsellQualtrics, UpgradeType } from '../../analytics/upsell.qualtrics';
import { ContextManager } from '../../context/manager';
import { useCorePageState } from '../../page/provider';
import DOMPurify from 'dompurify';

const { Button } = actions;
const { Toast } = overlays;
const sanitizer = DOMPurify.sanitize;

interface UpsellProps {
  content: UpsellContent;
  className?: string;
}

export const Upsell: React.FC<UpsellProps> = ({ content, className = '' }) => {
  const [toast, setToast] = useState<{ type: overlays.Notification; message: string }>();
  const context = ContextManager.getInstance().get();
  const corePageState = useCorePageState();

  const imageMap: Record<string, string> = {
    brands: brandsImage,
    features: featuresImage,
    priceclasses: priceclassesImage,
    channels: channelsImage,
    firstactivity: firstActivityImage,
    productleaderboard: productLeaderboardImage,
  };

  const pageUpgradeMap: Record<string, UpgradeType> = {
    brands: UpgradeType.BRANDS_PAGE_UPGRADE,
    features: UpgradeType.FEATURES_PAGE_UPGRADE,
    priceclasses: UpgradeType.PRICE_CLASSES_PAGE_UPGRADE,
    channels: UpgradeType.CHANNELS_PAGE_UPGRADE,
    firstactivity: UpgradeType.FIRST_ACTIVITY_PAGE_UPGRADE,
    productleaderboard: UpgradeType.PRODUCT_LEADERBOARD_PAGE_UPGRADE,
  };

  const handleContactClick = () => {
    triggerUpsellQualtrics(context, setToast, corePageState, pageUpgradeMap[content.imageKey]);
  };

  const selectedImage = content.imageKey && imageMap[content.imageKey] ? imageMap[content.imageKey] : featuresImage;

  return (
    <div className={`upsell-component ${className}`} data-test-id="upsell">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Illustration */}
        <div className=" rounded-lg p-6 text-center">
          <img
            src={selectedImage}
            alt="Features Preview"
            className="w-full h-auto rounded-lg"
            data-test-id="upsell-image"
          />
        </div>

        {/* Right Column - Upsell Content */}
        <div className=" rounded-lg p-6 text-xl">
          <div className="mb-6">
            <p className="text-gray-700 mb-4" data-test-id="upsell-heading">
              {content.heading}
            </p>

            {content.bulletPoints.length > 0 && (
              <ul className="space-y-2 mb-4" data-test-id="upsell-bullets">
                {content.bulletPoints.map((point, index) => (
                  <li key={point} className="flex items-start" data-test-id={`upsell-bullet-${index}`}>
                    <svg
                      className="text-green-500 mt-0.5 mr-2 flex-shrink-0 w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-gray-600" dangerouslySetInnerHTML={{ __html: sanitizer(point) }} />
                  </li>
                ))}
              </ul>
            )}

            <p className="text-gray-700 mb-6" data-test-id="upsell-cta">
              {content.callToAction}
            </p>
          </div>

          <Button mode="primary" onClick={handleContactClick} testId="upsell-contact-button">
            {content.buttonText}
          </Button>
        </div>
      </div>

      {toast && (
        <Toast type={toast.type} onComplete={() => setToast(undefined)}>
          <span data-test-id="upsell-toast-message">{toast.message}</span>
        </Toast>
      )}
    </div>
  );
};
